import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Collapse,
  TablePagination,
  Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ReceiptOutlinedIcon from "@material-ui/icons/ReceiptOutlined";
import TuneOutlinedIcon from "@material-ui/icons/TuneOutlined";
import StorefrontOutlinedIcon from "@material-ui/icons/StorefrontOutlined";
import SecurityOutlinedIcon from "@material-ui/icons/SecurityOutlined";
import PersonOutlineOutlinedIcon from "@material-ui/icons/PersonOutlineOutlined";
import ProgramLogDetailView from "./ProgramLogDetailView";
import { useTimezone } from "../../Context/TimezoneContext";
import {
  ACTION_STYLES,
  MODULE_CONFIGS,
  ROLE_BADGE_STYLES,
  actionLabel,
  formatReference,
  formatRole,
  formatDateTime,
  formatRelativeTime,
  getInitials,
  formatClientIp,
} from "./ProgramLogConstants";

const useStyles = makeStyles((theme) => ({
  tableCard: {
    borderRadius: 12,
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
    overflow: "auto",
    backgroundColor: "#ffffff",
  },
  tableHeadCell: {
    padding: theme.spacing(1.25, 1.5),
    fontWeight: 700,
    fontSize: "0.75rem",
    color: "#475569",
    backgroundColor: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
  },
  tableCell: {
    padding: theme.spacing(1.25, 1.5),
    fontSize: "0.8125rem",
    color: "#1e293b",
    borderBottom: "1px solid #f1f5f9",
  },
  logRow: {
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor: "#f8fafc",
    },
  },
  logRowExpanded: {
    backgroundColor: "#f8fafc",
  },
  expandCell: {
    width: 44,
    padding: theme.spacing(0.5, 1),
    textAlign: "center",
  },
  moduleChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.75rem",
    fontWeight: 600,
    padding: "3px 8px",
    borderRadius: 6,
    whiteSpace: "nowrap",
  },
  referenceCellWrapper: {
    display: "flex",
    alignItems: "center",
  },
  referenceBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "3px 9px",
    borderRadius: 6,
    border: "1px solid #e2e8f0",
    fontSize: "0.8125rem",
    lineHeight: 1.25,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.02)",
    transition: "all 0.15s ease",
  },
  referencePrefix: {
    fontWeight: 800,
    fontSize: "0.74rem",
    letterSpacing: "0.04em",
  },
  referenceSeparator: {
    fontWeight: 700,
    opacity: 0.6,
    fontSize: "0.75rem",
  },
  referenceNumber: {
    fontWeight: 700,
    color: "#0f172a",
    letterSpacing: "-0.01em",
  },
  referenceBulkBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 8px",
    borderRadius: 6,
    border: "1px solid",
    fontSize: "0.75rem",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  referenceText: {
    fontWeight: 600,
    fontSize: "0.8125rem",
    color: "#0f172a",
  },
  actorWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  actorAvatar: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    backgroundColor: "#e2e8f0",
    color: "#334155",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.7rem",
    fontWeight: 700,
    flexShrink: 0,
  },
  actorText: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  actorName: {
    fontWeight: 600,
    fontSize: "0.8125rem",
    color: "#1e293b",
    lineHeight: 1.2,
  },
  actorEmail: {
    fontSize: "0.7rem",
    color: "#64748b",
  },
}));

export default function ProgramLogTable({
  logs = [],
  totalCount = 0,
  page = 0,
  rowsPerPage = 20,
  onPageChange,
  onRowsPerPageChange,
  expandedRows = {},
  onToggleRow,
  currencySymbol = "$",
}) {
  const classes = useStyles();
  const { timezone } = useTimezone();

  const getModuleIcon = (mod) => {
    switch (mod) {
      case "Invoice":
        return <ReceiptOutlinedIcon style={{ fontSize: 16 }} />;
      case "StockAdjustment":
        return <TuneOutlinedIcon style={{ fontSize: 16 }} />;
      case "Item":
        return <StorefrontOutlinedIcon style={{ fontSize: 16 }} />;
      default:
        return null;
    }
  };

  return (
    <>
      <TableContainer component={Paper} elevation={0} className={`${classes.tableCard} slim-scrollbar`}>
        <Table aria-label="Program logs" size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeadCell} style={{ width: 44 }} />
              <TableCell className={classes.tableHeadCell}>Date &amp; Time</TableCell>
              <TableCell className={classes.tableHeadCell}>Module</TableCell>
              <TableCell className={classes.tableHeadCell}>Activity</TableCell>
              <TableCell className={classes.tableHeadCell}>Reference</TableCell>
              <TableCell className={classes.tableHeadCell}>User / Actor</TableCell>
              <TableCell className={classes.tableHeadCell}>Client IP</TableCell>
              <TableCell className={classes.tableHeadCell}>Role</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => {
              const isExpanded = expandedRows[log._id] === true;
              const hasDetail =
                (Array.isArray(log.items) && log.items.length > 0) ||
                (log.details && Object.keys(log.details).length > 0);

              const modConfig = MODULE_CONFIGS[log.module] || {
                label: log.module,
                color: "#334155",
                bgColor: "#f1f5f9",
                borderColor: "#cbd5e1",
              };

              const ref = formatReference(log);

              return (
                <React.Fragment key={log._id}>
                  <TableRow
                    className={`${classes.logRow} ${isExpanded ? classes.logRowExpanded : ""}`}
                    style={{ cursor: hasDetail ? "pointer" : "default" }}
                    onClick={() => hasDetail && onToggleRow(log._id)}
                  >
                    {/* Expand Chevron Column */}
                    <TableCell className={classes.expandCell}>
                      {hasDetail && (
                        <IconButton size="small" style={{ padding: 4 }}>
                          {isExpanded ? (
                            <ExpandLessIcon style={{ fontSize: 18, color: "#2563eb" }} />
                          ) : (
                            <ExpandMoreIcon style={{ fontSize: 18, color: "#64748b" }} />
                          )}
                        </IconButton>
                      )}
                    </TableCell>

                    {/* Date & Time */}
                    <TableCell className={classes.tableCell}>
                      <div style={{ fontWeight: 600, color: "#0f172a" }}>
                        {formatDateTime(log.createdAt, timezone)}
                      </div>
                      <div style={{ fontSize: "0.7rem", color: "#64748b", marginTop: 1 }}>
                        {formatRelativeTime(log.createdAt, timezone)}
                      </div>
                    </TableCell>

                    {/* Module */}
                    <TableCell className={classes.tableCell}>
                      <span
                        className={classes.moduleChip}
                        style={{
                          backgroundColor: modConfig.bgColor,
                          color: modConfig.color,
                          border: `1px solid ${modConfig.borderColor}`,
                        }}
                      >
                        {getModuleIcon(log.module)}
                        {modConfig.label}
                      </span>
                    </TableCell>

                    {/* Activity */}
                    <TableCell className={classes.tableCell}>
                      <Chip
                        size="small"
                        label={actionLabel(log.action)}
                        style={{
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          height: 24,
                          ...(ACTION_STYLES[log.action] || {}),
                        }}
                      />
                    </TableCell>

                    {/* Reference */}
                    <TableCell className={classes.tableCell}>
                      <div className={classes.referenceCellWrapper}>
                        {ref.isBulk ? (
                          <span
                            className={classes.referenceBulkBadge}
                            style={{
                              color: ref.color,
                              backgroundColor: ref.bgColor,
                              borderColor: ref.borderColor,
                            }}
                          >
                            {ref.number}
                          </span>
                        ) : ref.prefix ? (
                          <span
                            className={classes.referenceBadge}
                            style={{
                              backgroundColor: ref.bgColor,
                              borderColor: ref.borderColor,
                            }}
                          >
                            <span className={classes.referencePrefix} style={{ color: ref.color }}>
                              {ref.prefix}
                            </span>
                            <span className={classes.referenceSeparator} style={{ color: ref.color }}>
                              -
                            </span>
                            <span className={classes.referenceNumber}>
                              {ref.number}
                            </span>
                          </span>
                        ) : (
                          <span className={classes.referenceText}>{ref.number}</span>
                        )}
                      </div>
                    </TableCell>

                    {/* User / Actor */}
                    <TableCell className={classes.tableCell}>
                      <div className={classes.actorWrapper}>
                        <div className={classes.actorAvatar}>
                          {getInitials(log.userName || log.userEmail)}
                        </div>
                        <div className={classes.actorText}>
                          <span className={classes.actorName}>
                            {log.userName || "System User"}
                          </span>
                          {log.userEmail && (
                            <span className={classes.actorEmail}>{log.userEmail}</span>
                          )}
                        </div>
                      </div>
                    </TableCell>

                    <TableCell className={classes.tableCell}>
                      <span style={{ fontFamily: "monospace", fontSize: "0.75rem", color: "#475569" }}>
                        {formatClientIp(log.details?.ipAddress)}
                      </span>
                    </TableCell>

                    {/* Role */}
                    <TableCell className={classes.tableCell}>
                      {(() => {
                        const { label, tier } = formatRole(log.userRole);
                        const badgeStyle = ROLE_BADGE_STYLES[tier];
                        return (
                          <Chip
                            size="small"
                            icon={
                              tier === "user" ? (
                                <PersonOutlineOutlinedIcon style={{ fontSize: 13 }} />
                              ) : (
                                <SecurityOutlinedIcon style={{ fontSize: 13, color: badgeStyle.color }} />
                              )
                            }
                            label={label}
                            style={{
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              height: 22,
                              ...badgeStyle,
                            }}
                          />
                        );
                      })()}
                    </TableCell>
                  </TableRow>

                  {/* Expandable Detail View */}
                  {hasDetail && (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        style={{ paddingBottom: 0, paddingTop: 0, borderBottom: "1px solid #e2e8f0" }}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box my={1}>
                            <ProgramLogDetailView log={log} currencySymbol={currencySymbol} />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {totalCount > 0 && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="Rows per page:"
          style={{ borderTop: "1px solid #e2e8f0", backgroundColor: "#ffffff" }}
        />
      )}
    </>
  );
}
